import { createFileRoute, useParams, useSearch } from "@tanstack/react-router";
import { format } from "date-fns";
import { useEffect, useState } from "react";

import config from "@/config/client";
import type { IncidentDetailResponseDto } from "@/generated/model";

/**
 * Public print-ready page for incident PDFs (NAU-219c).
 *
 * - Lives OUTSIDE the `_authenticated` route group — the pdf-generator-api's
 *   headless browser has no session.
 * - Fetches data via the token-gated `/v1/out-of-range-incidents/:id/print`
 *   endpoint using the `?token=` query param.
 * - Adds `.pdf-ready` to <body> once the data is rendered so the headless
 *   browser knows it's safe to print.
 * - Uses A4 paper layout with carefully placed `break-inside: avoid` rules:
 *   the header repeats on every page (via `position: running()` is too
 *   esoteric — we let the document flow naturally and just guard sections
 *   from splitting mid-row).
 */

type PrintSearch = {
  token: string;
};

/**
 * The print page is normally only rendered by the headless PDF generator,
 * but it's also reachable directly if a user grabs the URL out of devtools
 * (or follows a stale share link). Surface meaningful copy in that case
 * instead of the raw `Print fetch failed: 401` string so anyone hitting it
 * understands what happened and what to do.
 */
type PrintError =
  | { kind: "missing-token" }
  | { kind: "expired-or-invalid"; status: number }
  | { kind: "not-found"; status: number }
  | { kind: "unexpected"; status?: number };

class PrintFetchError extends Error {
  constructor(
    public readonly kind: "expired-or-invalid" | "not-found" | "unexpected",
    public readonly status: number,
  ) {
    super(`Print fetch failed: ${status}`);
  }
}

function titleFor(err: PrintError): string {
  switch (err.kind) {
    case "missing-token":
      return "Print link is missing its token";
    case "expired-or-invalid":
      return "This print link has expired";
    case "not-found":
      return "Incident not found";
    case "unexpected":
      return "We couldn't load this incident";
  }
}

function messageFor(err: PrintError): string {
  switch (err.kind) {
    case "missing-token":
      return "Print links must be generated from the incident page in Naud. Open the incident and click “Download PDF” to get a fresh link.";
    case "expired-or-invalid":
      return "Print links are short-lived for security. Open the incident in Naud and click “Download PDF” again to generate a new one.";
    case "not-found":
      return "The incident in this link no longer exists. It may have been deleted.";
    case "unexpected":
      return err.status
        ? `Something went wrong while loading this incident (status ${err.status}). Please try generating the PDF again from the incident page.`
        : "Something went wrong while loading this incident. Please try generating the PDF again from the incident page.";
  }
}

/**
 * Compact, machine-readable reason for the headless PDF generator. The
 * service reads this off `body[data-pdf-error]` so a failed print surfaces
 * as a fast, diagnosable error instead of an opaque `.pdf-ready` timeout.
 */
function pdfErrorReason(err: PrintError): string {
  return "status" in err && err.status ? `${err.kind}:${err.status}` : err.kind;
}

export const Route = createFileRoute("/print/incidents/$incidentId")({
  validateSearch: (search: Record<string, unknown>): PrintSearch => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  component: IncidentPrintPage,
});

function IncidentPrintPage() {
  const { incidentId } = useParams({
    from: "/print/incidents/$incidentId",
  });
  const { token } = useSearch({ from: "/print/incidents/$incidentId" });

  const [data, setData] = useState<IncidentDetailResponseDto | null>(null);
  const [error, setError] = useState<PrintError | null>(null);

  useEffect(() => {
    if (!token) {
      setError({ kind: "missing-token" });
      return;
    }

    const url = `${config.apiUrl}/v1/out-of-range-incidents/${encodeURIComponent(incidentId)}/print?token=${encodeURIComponent(token)}`;

    fetch(url, { headers: { Accept: "application/json" } })
      .then(async (res) => {
        if (!res.ok) {
          // 401 from the backend covers both "invalid token" and "expired
          // token". We can't distinguish them client-side without the API
          // surfacing a reason code, so we use the same wording for both.
          if (res.status === 401) {
            throw new PrintFetchError("expired-or-invalid", res.status);
          }
          if (res.status === 404) {
            throw new PrintFetchError("not-found", res.status);
          }
          throw new PrintFetchError("unexpected", res.status);
        }
        return res.json();
      })
      .then((body: IncidentDetailResponseDto) => {
        setData(body);
      })
      .catch((err: unknown) => {
        if (err instanceof PrintFetchError) {
          setError({ kind: err.kind, status: err.status });
        } else {
          setError({ kind: "unexpected" });
        }
      });
  }, [incidentId, token]);

  // Once data is ready, mark the body so the pdf-generator's headless
  // browser knows it can print. Marking the body class (not a child div)
  // is more robust if Puppeteer waits on `body.pdf-ready`.
  useEffect(() => {
    if (data) {
      document.body.classList.add("pdf-ready");
    }
    return () => {
      document.body.classList.remove("pdf-ready");
    };
  }, [data]);

  // On failure, signal the headless browser so the PdfService can fail fast
  // with a diagnosable reason instead of waiting out the full selector
  // timeout. Mirrors the `.pdf-ready` convention.
  useEffect(() => {
    if (error) {
      document.body.dataset.pdfError = pdfErrorReason(error);
      document.body.classList.add("pdf-error");
    }
    return () => {
      document.body.classList.remove("pdf-error");
      delete document.body.dataset.pdfError;
    };
  }, [error]);

  if (error) {
    return (
      <div className="p-8 font-sans text-black max-w-[640px] mx-auto">
        <h1 className="text-xl font-semibold mb-3">{titleFor(error)}</h1>
        <p className="text-neutral-700">{messageFor(error)}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-8 font-sans text-black">
        <p>Loading…</p>
      </div>
    );
  }

  return <IncidentPrintLayout incident={data} />;
}

function IncidentPrintLayout({
  incident,
}: {
  incident: IncidentDetailResponseDto;
}) {
  const isResolved = incident.resolvedAt !== null;
  const openedAt = format(new Date(incident.openedAt), "d MMMM yyyy, HH:mm");
  const resolvedAt = incident.resolvedAt
    ? format(new Date(incident.resolvedAt), "d MMMM yyyy, HH:mm")
    : null;
  const triggerTemp = incident.firstReading.temperature;
  const targetMin = incident.firstReading.thresholdMinAtLog;
  const targetMax = incident.firstReading.thresholdMaxAtLog;
  const recordedAt = format(
    new Date(incident.firstReading.recordedAt),
    "d MMMM yyyy, HH:mm",
  );
  const generatedAt = format(new Date(), "d MMMM yyyy, HH:mm");

  return (
    <div className="incident-print bg-white text-black">
      <style>{PRINT_STYLES}</style>

      <main className="sheet">
        {/* HEADER — repeats visually via running-header pattern via CSS */}
        <header className="doc-header">
          <div className="doc-header-row">
            <div>
              <h1 className="doc-title">Out-of-range incident</h1>
              <p className="doc-subtitle">{incident.coldStorageName}</p>
            </div>
            <div className="doc-status-block">
              <span
                className={`status-pill ${isResolved ? "resolved" : "open"}`}
              >
                {isResolved ? "Resolved" : "Open"}
              </span>
              <div className="doc-id">
                Incident ID:&nbsp;
                <span className="mono">{incident.id.slice(0, 8)}</span>
              </div>
            </div>
          </div>
          <hr className="doc-rule" />
        </header>

        {/* SUMMARY GRID */}
        <section className="summary-section">
          <div className="summary-grid">
            <Field label="Opened" value={openedAt} />
            <Field
              label="Resolved"
              value={resolvedAt ?? "—"}
              muted={!resolvedAt}
            />
            <Field
              label="Resolved by"
              value={incident.resolvedBy ? userLabel(incident.resolvedBy) : "—"}
              muted={!incident.resolvedBy}
            />
            <Field
              label="Target range"
              value={
                targetMin !== null && targetMax !== null
                  ? `${targetMin}°C to ${targetMax}°C`
                  : "—"
              }
            />
          </div>
        </section>

        {/* TRIGGERING READING — highlighted callout */}
        <section className="callout callout-warning avoid-break">
          <div className="callout-label">Triggering reading</div>
          <div className="callout-body">
            <span className="big-number">{triggerTemp}°C</span>
            <span className="callout-meta">
              Recorded {recordedAt}
              {incident.firstReading.notes
                ? ` · ${incident.firstReading.notes}`
                : ""}
            </span>
          </div>
        </section>

        {/* RESOLUTION — only when resolved */}
        {isResolved && (
          <section className="callout callout-success avoid-break">
            <div className="callout-label">Resolution</div>
            <div className="callout-body">
              <span className="callout-meta">
                Signed off by{" "}
                {incident.resolvedBy
                  ? userLabel(incident.resolvedBy)
                  : "unknown manager"}{" "}
                · {resolvedAt}
              </span>
              {incident.resolutionNotes ? (
                <p className="callout-note">{incident.resolutionNotes}</p>
              ) : (
                <p className="callout-note muted">
                  No remedial-action notes recorded.
                </p>
              )}
            </div>
          </section>
        )}

        {/* TIMELINE */}
        <section className="timeline-section">
          <h2 className="section-heading">Reading timeline</h2>
          <table className="timeline-table">
            <thead>
              <tr>
                <th className="col-time">Recorded</th>
                <th className="col-temp">Reading</th>
                <th className="col-threshold">Target range</th>
                <th className="col-status">Status</th>
                <th className="col-recorder">Recorded by</th>
                <th className="col-notes">Notes</th>
              </tr>
            </thead>
            <tbody>
              {incident.entries.map((entry) => (
                <tr key={entry.id} className="timeline-row">
                  <td className="col-time">
                    {format(new Date(entry.recordedAt), "d MMM, HH:mm")}
                  </td>
                  <td className="col-temp">{entry.temperature}°C</td>
                  <td className="col-threshold mono-narrow">
                    {entry.thresholdMinAtLog !== null &&
                    entry.thresholdMaxAtLog !== null
                      ? `${entry.thresholdMinAtLog} – ${entry.thresholdMaxAtLog}`
                      : "—"}
                  </td>
                  <td className="col-status">
                    <span
                      className={`row-pill ${entry.isOutOfRange ? "out" : "in"}`}
                    >
                      {entry.isOutOfRange ? "Out of range" : "In range"}
                    </span>
                  </td>
                  <td className="col-recorder">
                    {entry.recordedBy ? userLabel(entry.recordedBy) : "—"}
                  </td>
                  <td className="col-notes">{entry.notes ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* FOLLOW-UP TASKS — part of the remedial-action audit trail (AC#5/AC#6) */}
        <section className="followups-section">
          <h2 className="section-heading">Remedial follow-up tasks</h2>
          {incident.followUpActions.length === 0 ? (
            <p className="empty-note">
              No follow-up tasks were created for this incident.
            </p>
          ) : (
            <ol className="followups-list">
              {incident.followUpActions.map((task) => (
                <FollowUpTaskItem key={task.id} task={task} />
              ))}
            </ol>
          )}
        </section>

        {/* FOOTER (visible at end of doc) */}
        <footer className="doc-footer">
          <span>Generated {generatedAt}</span>
          <span>
            {incident.entries.length} readings ·{" "}
            {incident.followUpActions.length} follow-up task
            {incident.followUpActions.length === 1 ? "" : "s"}
          </span>
        </footer>
      </main>
    </div>
  );
}

function FollowUpTaskItem({
  task,
}: {
  task: IncidentDetailResponseDto["followUpActions"][number];
}) {
  const statusLabel: Record<typeof task.status, string> = {
    OPEN: "Open",
    OVERDUE: "Overdue",
    WAITING_APPROVAL: "Waiting for sign-off",
    COMPLETED: "Completed",
    CANCELLED: "Cancelled",
  };
  const statusClass: Record<typeof task.status, string> = {
    OPEN: "pill-neutral",
    OVERDUE: "pill-error",
    WAITING_APPROVAL: "pill-brand",
    COMPLETED: "pill-success",
    CANCELLED: "pill-muted",
  };
  const assigneeLabel = task.assigneeUser
    ? userLabel(task.assigneeUser)
    : task.assigneeRole
      ? humanRole(task.assigneeRole)
      : "Unassigned";
  const dueLabel = task.dueDate
    ? task.dueTime
      ? `${format(new Date(task.dueDate), "d MMM yyyy")}, ${task.dueTime}`
      : format(new Date(task.dueDate), "d MMM yyyy")
    : "No due date";
  const createdLabel = format(new Date(task.createdAt), "d MMM yyyy, HH:mm");

  return (
    <li className="followup-item">
      <div className="followup-row">
        <span className="followup-description">{task.description}</span>
        <span className={`row-pill ${statusClass[task.status]}`}>
          {statusLabel[task.status]}
        </span>
      </div>
      <div className="followup-meta">
        <span>
          <span className="meta-label">Assigned to:</span> {assigneeLabel}
        </span>
        <span>
          <span className="meta-label">Due:</span> {dueLabel}
        </span>
        <span>
          <span className="meta-label">Created:</span> {createdLabel}
        </span>
      </div>
      {task.completedAt && (
        <div className="followup-resolution">
          <div className="followup-meta">
            <span>
              <span className="meta-label">Completed:</span>{" "}
              {format(new Date(task.completedAt), "d MMM yyyy, HH:mm")}
            </span>
            {task.completedBy && (
              <span>
                <span className="meta-label">By:</span>{" "}
                {userLabel(task.completedBy)}
              </span>
            )}
          </div>
          {task.completionNotes && (
            <p className="followup-notes">{task.completionNotes}</p>
          )}
        </div>
      )}
    </li>
  );
}

function humanRole(role: string): string {
  return role
    .split("_")
    .map((p) => p[0] + p.slice(1).toLowerCase())
    .join(" ");
}

function Field({
  label,
  value,
  muted,
}: {
  label: string;
  value: string;
  muted?: boolean;
}) {
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <span className={`field-value ${muted ? "muted" : ""}`}>{value}</span>
    </div>
  );
}

function userLabel(u: {
  firstName: string | null;
  lastName: string | null;
  email: string;
}): string {
  const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return name || u.email;
}

const PRINT_STYLES = `
  @page {
    size: A4;
    margin: 18mm 16mm 22mm 16mm;
  }
  @media print {
    html, body {
      background: white !important;
      color: black !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
  .incident-print {
    color-scheme: light;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
    color: #111;
  }
  .incident-print .sheet {
    max-width: 178mm;
    margin: 0 auto;
    padding: 4mm;
    font-size: 10.5pt;
    line-height: 1.4;
  }

  .doc-header { margin-bottom: 6mm; }
  .doc-header-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12mm;
  }
  .doc-title { font-size: 18pt; font-weight: 600; margin: 0 0 1mm 0; letter-spacing: -0.01em; }
  .doc-subtitle { font-size: 13pt; color: #444; margin: 0; }
  .doc-status-block { text-align: right; }
  .doc-id { margin-top: 1.5mm; font-size: 8.5pt; color: #555; }
  .doc-rule { border: 0; border-top: 0.5pt solid #000; margin: 3mm 0 0 0; }

  .mono { font-family: "SF Mono", Menlo, Consolas, monospace; }
  .mono-narrow { font-family: "SF Mono", Menlo, Consolas, monospace; font-size: 9.5pt; }

  .status-pill {
    display: inline-block;
    padding: 1mm 3mm;
    font-size: 9pt;
    font-weight: 600;
    border-radius: 2mm;
    border: 0.5pt solid;
  }
  .status-pill.open { background: #fff7ed; border-color: #fb923c; color: #9a3412; }
  .status-pill.resolved { background: #ecfdf5; border-color: #34d399; color: #065f46; }

  .summary-section { margin-bottom: 6mm; }
  .summary-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3mm 8mm;
  }
  .field { display: flex; flex-direction: column; gap: 0.6mm; }
  .field-label { font-size: 7.5pt; text-transform: uppercase; letter-spacing: 0.05em; color: #666; }
  .field-value { font-size: 10.5pt; font-weight: 500; }
  .field-value.muted { color: #999; }

  .callout {
    border: 0.5pt solid;
    border-radius: 2mm;
    padding: 3mm 4mm;
    margin-bottom: 5mm;
  }
  .callout.avoid-break { break-inside: avoid; page-break-inside: avoid; }
  .callout-warning { background: #fff7ed; border-color: #fb923c; }
  .callout-success { background: #ecfdf5; border-color: #34d399; }
  .callout-label {
    font-size: 8pt;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #555;
    margin-bottom: 1.5mm;
  }
  .callout-body { display: flex; flex-direction: column; gap: 1.5mm; }
  .big-number { font-size: 22pt; font-weight: 600; line-height: 1; }
  .callout-meta { font-size: 9.5pt; color: #333; }
  .callout-note { font-size: 10pt; margin: 1.5mm 0 0 0; white-space: pre-wrap; }
  .callout-note.muted { color: #888; font-style: italic; }

  .section-heading {
    font-size: 12pt;
    font-weight: 600;
    margin: 0 0 2mm 0;
    padding-bottom: 1mm;
    border-bottom: 0.5pt solid #000;
  }
  .timeline-section { margin-bottom: 6mm; }
  .timeline-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 9.5pt;
  }
  .timeline-table thead { display: table-header-group; }
  .timeline-table thead tr {
    background: #f3f4f6;
  }
  .timeline-table th {
    text-align: left;
    font-size: 8pt;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #444;
    padding: 1.5mm 2mm;
    border-bottom: 0.5pt solid #000;
  }
  .timeline-table td {
    padding: 1.8mm 2mm;
    border-bottom: 0.25pt solid #d4d4d8;
    vertical-align: top;
  }
  .timeline-row { break-inside: avoid; page-break-inside: avoid; }
  .col-time { width: 26mm; }
  .col-temp { width: 14mm; font-weight: 500; }
  .col-threshold { width: 22mm; color: #555; }
  .col-status { width: 22mm; }
  .col-recorder { width: 32mm; }
  .col-notes { color: #333; }
  .row-pill {
    display: inline-block;
    padding: 0.5mm 2mm;
    font-size: 8pt;
    font-weight: 600;
    border-radius: 1.5mm;
    border: 0.5pt solid;
    white-space: nowrap;
  }
  .row-pill.in { background: #ecfdf5; border-color: #34d399; color: #065f46; }
  .row-pill.out { background: #fff7ed; border-color: #fb923c; color: #9a3412; }
  .row-pill.pill-neutral { background: #f4f4f5; border-color: #a1a1aa; color: #3f3f46; }
  .row-pill.pill-error { background: #fee2e2; border-color: #ef4444; color: #991b1b; }
  .row-pill.pill-brand { background: #ede9fe; border-color: #8b5cf6; color: #4c1d95; }
  .row-pill.pill-success { background: #ecfdf5; border-color: #34d399; color: #065f46; }
  .row-pill.pill-muted { background: #f4f4f5; border-color: #d4d4d8; color: #71717a; }

  .followups-section { margin-bottom: 6mm; }
  .followups-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 3mm;
  }
  .followup-item {
    break-inside: avoid;
    page-break-inside: avoid;
    border: 0.5pt solid #d4d4d8;
    border-radius: 1.5mm;
    padding: 2.5mm 3mm;
  }
  .followup-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 4mm;
    margin-bottom: 1.5mm;
  }
  .followup-description {
    flex: 1;
    font-size: 10.5pt;
    font-weight: 500;
    color: #111;
  }
  .followup-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5mm 5mm;
    font-size: 9pt;
    color: #444;
  }
  .followup-meta .meta-label {
    color: #888;
    font-weight: 500;
  }
  .followup-resolution {
    margin-top: 2mm;
    padding-top: 2mm;
    border-top: 0.25pt dashed #d4d4d8;
  }
  .followup-notes {
    margin: 1.5mm 0 0 0;
    font-size: 9.5pt;
    color: #222;
    white-space: pre-wrap;
  }
  .empty-note {
    font-size: 9.5pt;
    color: #888;
    font-style: italic;
    margin: 0;
  }

  .doc-footer {
    margin-top: 6mm;
    padding-top: 2mm;
    border-top: 0.5pt solid #000;
    display: flex;
    justify-content: space-between;
    font-size: 8.5pt;
    color: #555;
  }
`;
